import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiError, formatEuro } from "../../lib/api";
import AdminShell from "./AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export default function RatesEditor() {
  const [rates, setRates] = useState([]);
  const [edited, setEdited] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/rates");
      // Backward-compat: legacy response was a bare array.
      if (Array.isArray(r.data)) {
        setRates(r.data);
        setCanEdit(false);
      } else {
        setRates(r.data?.rates || []);
        setCanEdit(!!r.data?.can_edit);
      }
      setEdited({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setVal = (id, key, v) => {
    setEdited((prev) => ({ ...prev, [id]: { ...prev[id], [key]: v } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const ids = Object.keys(edited);
      for (const id of ids) {
        const patch = edited[id];
        const body = {};
        if (patch.valore_minimo_mq !== undefined) body.valore_minimo_mq = Number(patch.valore_minimo_mq);
        if (patch.valore_massimo_mq !== undefined) body.valore_massimo_mq = Number(patch.valore_massimo_mq);
        await api.put(`/admin/rates/${id}`, body);
      }
      toast.success(`${ids.length} tariffe aggiornate`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const grouped = {};
  rates.forEach((r) => {
    if (!grouped[r.zona]) grouped[r.zona] = {};
    grouped[r.zona][r.sub_fascia] = r;
  });

  return (
    <AdminShell
      title="Tariffe Canone Concordato Milano"
      actions={
        canEdit ? (
          <Button
            onClick={save}
            disabled={saving || Object.keys(edited).length === 0}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="save-rates"
          >
            <Save size={14} className="mr-2" />
            {saving ? "Salvataggio..." : `Salva (${Object.keys(edited).length})`}
          </Button>
        ) : (
          <span className="text-xs text-slate-500 italic" data-testid="rates-readonly-badge">
            Sola lettura — modifica riservata all&apos;amministratore principale
          </span>
        )
      }
    >
      <p className="text-sm text-slate-500 mb-6 max-w-2xl">
        Valori €/mq <strong>mensili</strong> per zona e sub-fascia. Il canone minimo è
        (mq riparametrati × €/mq min). Il canone massimo è (mq × €/mq max) aumentato o
        ridotto della percentuale di maggiorazioni/riduzioni.
      </p>
      {!canEdit && !loading && (
        <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3 text-sm text-amber-900" data-testid="rates-locked-banner">
          <strong>Tariffe bloccate.</strong> Puoi consultare i valori €/mq ma la modifica è riservata all&apos;account <code className="bg-white/70 px-1.5 py-0.5 rounded border border-amber-200">milano@unioncasa.org</code> per garantire l&apos;integrità dell&apos;Accordo territoriale.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Caricamento...</div>
        ) : (
          <table className="w-full text-sm" data-testid="rates-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 text-left font-medium">Zona</th>
                {[1, 2, 3].map((sf) => (
                  <th key={sf} colSpan={2} className="px-4 py-3 text-center font-medium border-l border-slate-200">
                    Sub-fascia {sf}
                  </th>
                ))}
              </tr>
              <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-2" />
                {[1, 2, 3].flatMap((sf) => [
                  <th key={`${sf}-min`} className="px-4 py-2 text-center font-medium border-l border-slate-200">Min €/mq</th>,
                  <th key={`${sf}-max`} className="px-4 py-2 text-center font-medium">Max €/mq</th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map((zona) => (
                <tr key={zona} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900 bg-slate-50/50">Zona {zona}</td>
                  {[1, 2, 3].map((sf) => {
                    const r = grouped[zona]?.[sf];
                    if (!r) return <td key={sf} colSpan={2} />;
                    const ev = edited[r.id] || {};
                    return (
                      <Fragment key={`${zona}-${sf}`}>
                        <td className="px-3 py-2 border-l border-slate-100">
                          <Input
                            type="number"
                            step="0.01"
                            defaultValue={r.valore_minimo_mq}
                            onChange={(e) => setVal(r.id, "valore_minimo_mq", e.target.value)}
                            className={`w-full text-right tabular-nums ${!canEdit ? "bg-slate-100 cursor-not-allowed" : ""}`}
                            readOnly={!canEdit}
                            data-testid={`rate-${zona}-${sf}-min`}
                          />
                          <div className="text-[10px] text-slate-400 text-right mt-0.5">
                            {formatEuro(ev.valore_minimo_mq ?? r.valore_minimo_mq)}/mese
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            defaultValue={r.valore_massimo_mq}
                            onChange={(e) => setVal(r.id, "valore_massimo_mq", e.target.value)}
                            className={`w-full text-right tabular-nums ${!canEdit ? "bg-slate-100 cursor-not-allowed" : ""}`}
                            readOnly={!canEdit}
                            data-testid={`rate-${zona}-${sf}-max`}
                          />
                          <div className="text-[10px] text-slate-400 text-right mt-0.5">
                            {formatEuro(ev.valore_massimo_mq ?? r.valore_massimo_mq)}/mese
                          </div>
                        </td>
                      </Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
